import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { Fragment } from "react/jsx-runtime";
import { cn } from "@repo/utils";
import { Button } from "../../button/button";
import { SelectInput } from "../../inputs/select/select";

type Props = {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSizeOptions?: number[];
  className?: string;
  showPageSizeSelector?: boolean;
  showResultsInfo?: boolean;
};

export const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  className,
  showPageSizeSelector = true,
  showResultsInfo = true,
}: Props) => {
  const startIndex = currentPage * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);
  const canGoPrevious = currentPage > 0;
  const canGoNext = currentPage < totalPages - 1;

  const goToFirstPage = () => onPageChange(0);
  const goToPreviousPage = () => onPageChange(Math.max(0, currentPage - 1));
  const goToNextPage = () => onPageChange(Math.min(totalPages - 1, currentPage + 1));
  const goToLastPage = () => onPageChange(totalPages - 1);

  if (totalPages === 0) return null;

  return (
    <div className={cn("flex items-center justify-between", className)}>
      {showResultsInfo && (
        <div className="text-sm text-gray-600">
          Showing <span className="font-medium">{startIndex + 1}</span> to{" "}
          <span className="font-medium">{endIndex}</span> of{" "}
          <span className="font-medium">{totalItems}</span> results
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="icon_sm"
          onClick={goToFirstPage}
          disabled={!canGoPrevious}
          aria-label="Go to first page"
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon_sm"
          onClick={goToPreviousPage}
          disabled={!canGoPrevious}
          aria-label="Go to previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <span className="text-sm text-gray-600">
          Page <span className="font-medium">{currentPage + 1}</span> of{" "}
          <span className="font-medium">{totalPages}</span>
        </span>

        <Button
          variant="outline"
          size="icon_sm"
          onClick={goToNextPage}
          disabled={!canGoNext}
          aria-label="Go to next page"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon_sm"
          onClick={goToLastPage}
          disabled={!canGoNext}
          aria-label="Go to last page"
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>

        {showPageSizeSelector && (
          <Fragment>
            <SelectInput
              options={pageSizeOptions.map((size) => ({
                label: `${size}`,
                value: size.toString(),
              }))}
              placeholder="Select page size"
              value={pageSize.toLocaleString()}
              onChange={(value) => {
                onPageSizeChange(Number(value));
              }}
              className="max-h-5 max-w-20"
              aria-label="Select page size"
            />
            <span className="ml-2 text-sm text-gray-600">per page</span>
          </Fragment>
        )}
      </div>
    </div>
  );
};
